import React, { useState } from "react";
import { LeadCard, Sec, Proposed, Fold } from "./parts";
import { agreementState, agreementLatest, quoteView, conversationView } from "./leadModel.mjs";
import { quoteSourceWords } from "../../timelineModel.mjs";
import AgreementBuilder, { AGREEMENT_FIELDS } from "./AgreementBuilder";
import { shortDate, fullName } from "../format.mjs";
import { PricingRoomStrip, AgreementRoomStrip } from "../../rooms/RoomStrips";

/*
 * AGREEMENT + LEGAL: "The agreement" (frame 1d, Northwind).
 *
 *   - THE DOCUMENT'S STATE, only from what PandaDoc emailed us (act.document, PR
 *     #154/#155): "Viewed Sep 30 by Tom Becker · 3 views · not signed", then "First
 *     viewed Sep 29 · read only from PandaDoc’s notification emails · PandaDoc
 *     notification email ↗". One view prints no count (until the log's backfill has
 *     run, one is only a floor), and with no notification at all it says so rather
 *     than "sent, never viewed", which nothing can prove. A viewed or signed NDA
 *     never counts: the server leaves NDAs out. Expires is a Proposed tag: PandaDoc
 *     tells us no expiry.
 *   - WHO'S ON THE PAPER: everyone at the company, by name and title. The ROLES
 *     (signer, legal, champion) are Proposed, so the heading wears the tag and no
 *     role is printed: nothing stores one.
 *   - THE LATEST ON THE AGREEMENT'S THREAD from anyone at the company (reality.txt
 *     [16]): who and when, and its first line, or "in the conversation above" when
 *     Latest conversation is already showing that very message. Nothing counts
 *     redlines, so there is no "2 items".
 *   - WHAT WE QUOTED and THE AGREEMENT BUILDER, one click away, folded.
 *
 * Props:
 *   record   the shell's bundle: act (document, facts.stage_entered_at),
 *            company.contacts, timeline.rows, view.main (is the conversation
 *            shown above), zones
 *   ops      ops.reload (the builder's saved overrides)
 */
export default function Agreement({ record, ops }) {
  const [fold, setFold] = useState(null);
  const doc = (record.act && record.act.document) || null;
  const facts = (record.act && record.act.facts) || {};
  const state = agreementState(doc, { now: record.now });
  const people = ((record.company && record.company.contacts) || []).filter((c) => c.kind !== "junk");
  const latest = agreementLatest(record.timeline.rows, { company: record.company, contact: record.contact });
  const quote = quoteView(record.timeline.rows, { stageAt: null, company: record.company });
  const d = (ts) => shortDate(ts, { now: record.now });
  // No pricing email: the fee first went out inside the agreement (Pluto), which
  // Deal and money's row already words ("In the agreement · Sep 27").
  const quotedRow = (record.deal || []).find((r) => r.key === "quoted");
  const inAgreement = !quote && quotedRow && !quotedRow.empty && quotedRow.value ? quotedRow.value.charAt(0).toLowerCase() + quotedRow.value.slice(1) : null;

  // "in the conversation above": the same message is the one Latest conversation leads with.
  const shown = (record.view.main || []).includes("latest_conversation")
    ? conversationView(record.timeline.rows, { company: record.company, contact: record.contact, zones: record.zones, now: record.now })
    : null;
  const above = latest && shown && shown.mode === "theirs" && String(shown.lead.id) === String(latest.id);

  return (
    <LeadCard
      label="The agreement"
      meta={
        <span className="rec-proposed-wrap">
          Expires<span className="rec-proposed">Proposed</span>
        </span>
      }
    >
      <div>
        <div className="rec-headline">{state.headline}</div>
        <div className="rec-sub">
          {state.sub.join(" · ")}
          {state.url && (
            <>
              {" · "}
              <a href={state.url} target="_blank" rel="noopener noreferrer">PandaDoc notification email ↗</a>
            </>
          )}
        </div>
      </div>

      {people.length > 0 && (
        <div className="rec-lead__paper rec-sep">
          {people.map((p, i) => (
            <div key={p.id}>
              <div className="rec-lead__small">{i === 0 ? <Proposed><span>On the paper</span></Proposed> : " "}</div>
              <div className="rec-strong">{fullName(p)}</div>
              {p.job_title && <div className="rec-sub">{p.job_title}</div>}
            </div>
          ))}
        </div>
      )}

      {latest && (
        <Sec>
          <span>
            Latest on the agreement’s thread: <b className="rec-strong">{latest.who}, {d(latest.at)}</b>
            {above ? ", in the conversation above" : latest.text ? ` · ${latest.text}` : ""}
          </span>
        </Sec>
      )}

      {/* The draft and the PandaDoc link on the company's prospect room (src/rooms). */}
      <AgreementRoomStrip company={record.company} />

      <div className="rec-lead__folds">
        <Fold
          title="What we quoted"
          sub={quote ? lowerFirst(quoteSourceWords(quote, d)) : inAgreement ? inAgreement : "no pricing email on record"}
          open={fold === "quote"}
          onToggle={() => setFold(fold === "quote" ? null : "quote")}
        >
          {quote ? (
            <div className="rec-stack">
              {quote.intro && <span className="rec-meta">{quote.intro}</span>}
              {quote.terms.map((t) => (
                <span key={t} className="rec-strong">{t}</span>
              ))}
              {facts.stage_entered_at && <span className="rec-meta">Agreement + legal since {d(facts.stage_entered_at)}</span>}
              <PricingRoomStrip company={record.company} terms={quote.terms} />
            </div>
          ) : (
            <div className="rec-empty">{inAgreement ? "No pricing email went out: the fee first went out inside the agreement." : "No email on this company’s timeline quotes our pricing."}</div>
          )}
        </Fold>
        <Fold
          title="Agreement builder"
          sub={`.docx from what we quoted · ${AGREEMENT_FIELDS} fields, each in writing, inferred or not in record`}
          open={fold === "builder"}
          onToggle={() => setFold(fold === "builder" ? null : "builder")}
        >
          <AgreementBuilder company={record.company} onChanged={ops.reload} />
        </Fold>
      </div>
    </LeadCard>
  );
}

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
